/**
 * The phone's own store for offline use (IndexedDB, database "ops-offline"):
 *   cache    saved API answers by path (job packs, My shifts, kits, photos lists)
 *   files    saved documents by path (TMP, TGS, permit, SWMS)
 *   outbox   actions saved while offline, sent in order when back online
 *   results  answers to sent actions, for later actions that need them (an incident's photos need its id)
 * Falls back to memory when IndexedDB is not available (private browsing on some phones), so the app still works,
 * just without surviving a reload.
 */
export type StoreName = "cache" | "files" | "outbox" | "results";
const STORES: StoreName[] = ["cache", "files", "outbox", "results"];

export interface KV {
  get<T>(store: StoreName, key: string): Promise<T | undefined>;
  put(store: StoreName, key: string, value: unknown): Promise<void>;
  del(store: StoreName, key: string): Promise<void>;
  all<T>(store: StoreName): Promise<{ key: string; value: T }[]>;
}

export function memoryKV(): KV {
  const m = new Map<StoreName, Map<string, unknown>>(STORES.map((s) => [s, new Map()]));
  return {
    async get(s, k) {
      return m.get(s)!.get(k) as never;
    },
    async put(s, k, v) {
      m.get(s)!.set(k, v);
    },
    async del(s, k) {
      m.get(s)!.delete(k);
    },
    async all(s) {
      return [...m.get(s)!.entries()].map(([key, value]) => ({ key, value: value as never }));
    },
  };
}

function idbKV(): KV | null {
  if (typeof indexedDB === "undefined") return null;
  let opened: Promise<IDBDatabase> | null = null;
  const db = () =>
    (opened ??= new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open("ops-offline", 1);
      req.onupgradeneeded = () => {
        for (const s of STORES) if (!req.result.objectStoreNames.contains(s)) req.result.createObjectStore(s);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    }));
  const run = async <T>(store: StoreName, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest): Promise<T> => {
    const d = await db();
    return new Promise<T>((resolve, reject) => {
      const tx = d.transaction(store, mode);
      const req = fn(tx.objectStore(store));
      tx.oncomplete = () => resolve(req.result as T);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  };
  return {
    get: (s, k) => run(s, "readonly", (o) => o.get(k)),
    put: async (s, k, v) => void (await run(s, "readwrite", (o) => o.put(v, k))),
    del: async (s, k) => void (await run(s, "readwrite", (o) => o.delete(k))),
    async all<T>(s: StoreName) {
      const d = await db();
      return new Promise<{ key: string; value: T }[]>((resolve, reject) => {
        const out: { key: string; value: T }[] = [];
        const req = d.transaction(s, "readonly").objectStore(s).openCursor();
        req.onsuccess = () => {
          const c = req.result;
          if (!c) return resolve(out);
          out.push({ key: String(c.key), value: c.value as T });
          c.continue();
        };
        req.onerror = () => reject(req.error);
      });
    },
  };
}

/** The store, or memory when IndexedDB fails to open. */
let kv: KV | null = null;
export function store(): KV {
  if (kv) return kv;
  const idb = idbKV();
  const mem = memoryKV();
  if (!idb) return (kv = mem);
  // If IndexedDB errors (blocked, quota), quietly use memory from then on.
  let broken = false;
  const guard = <A extends unknown[], R>(f: (...a: A) => Promise<R>, g: (...a: A) => Promise<R>) => async (...a: A): Promise<R> => {
    if (broken) return g(...a);
    try {
      return await f(...a);
    } catch {
      broken = true;
      return g(...a);
    }
  };
  kv = { get: guard(idb.get, mem.get), put: guard(idb.put, mem.put), del: guard(idb.del, mem.del), all: guard(idb.all, mem.all) } as KV;
  return kv;
}
/** For tests. */
export const useStore = (s: KV) => void (kv = s);
